import React, { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { CalendarDays } from "lucide-react";

import { DateField } from "../../../../components/src/form";

interface FoNavDateRangeChipProps {
    startDate: string;
    endDate: string;
    onChangeStart: (v: string) => void;
    onChangeEnd: (v: string) => void;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The shared `PeriodSelector` keeps its range chip private and drives the dates from the query string
 * itself. This page instead derives the start date from the end date (previous business day), so it owns
 * the dates and only needs the chip.
 */
export const FoNavDateRangeChip = ({ startDate, endDate, onChangeStart, onChangeEnd }: FoNavDateRangeChipProps): React.ReactElement => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocMouseDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const chipBase =
        "inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-sky-300 tabular-nums";
    let chipState = " bg-white border-gray-300 text-gray-700 hover:bg-sky-50 hover:border-sky-300";
    if (open) {
        chipState = " bg-sky-100 border-sky-400 text-sky-700";
    }

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                className={chipBase + chipState}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <CalendarDays size={14} className="text-slate-500" aria-hidden />
                <span>
                    {startDate} <span className="text-slate-400 px-1">→</span> {endDate}
                </span>
            </button>
            {open ? (
                <div
                    role="dialog"
                    className="absolute left-0 top-full mt-2 z-30 w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
                >
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">Custom range</div>
                    <Formik
                        enableReinitialize
                        initialValues={{ startDate, endDate }}
                        onSubmit={() => {
                            /* No submit button */
                        }}
                        validate={(values) => {
                            if (values.startDate && DATE_RE.test(values.startDate) && values.startDate !== startDate) {
                                onChangeStart(values.startDate);
                            }
                            if (values.endDate && DATE_RE.test(values.endDate) && values.endDate !== endDate) {
                                onChangeEnd(values.endDate);
                            }
                            return {};
                        }}
                    >
                        {() => (
                            <Form autoComplete="off" className="flex flex-col gap-2 mb-0">
                                <DateField className="mb-0" name="startDate" label="From" disabled={false} />
                                <DateField className="mb-0" name="endDate" label="To" disabled={false} />
                            </Form>
                        )}
                    </Formik>
                    <div className="mt-2 flex justify-end">
                        <button
                            type="button"
                            className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-sky-300"
                            onClick={() => setOpen(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
